import { omitBy } from "remeda";
import type {
  CreateEditJobOptions,
  Job,
  JobCalcsPreviewResponse,
  JobMarkersDto,
  JobReport,
  JobsListResponse,
  JobTypeName,
  NotesData,
} from "../types/jobs";
import { apiService } from "./apiService";
import { getErrorMessage } from "./errorHandling";

export const createJob = async ({ data, isMultipart }: { data: unknown; isMultipart?: boolean }) => {
  try {
    return await apiService<Job>({
      method: "post",
      url: "/jobs/create/",
      data,
      isMultipart,
      timeout: 90000,
    });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const deleteJob = async (id: number) => {
  try {
    return await apiService({ method: "delete", url: `/jobs/destroy/${id}/` });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const getJob = async (id: string) => {
  try {
    return await apiService<Job>({ method: "get", url: `/jobs/detail/${id}/` });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const getJobReport = async (id: string) => {
  try {
    return await apiService<JobReport>({ method: "get", url: `/jobs/check_job_report/${id}/` });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const getJobs = async ({
  jobNumber,
  page,
  jobType,
}: {
  jobNumber: string;
  page: number;
  jobType?: "composite" | "mechanical";
}) => {
  try {
    const params = omitBy({ job_number: jobNumber, page, job_type: jobType }, (value) => !value);
    return await apiService<JobsListResponse>({ method: "get", url: "/jobs/list/", params });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const getAdditionalNotes = async (jobType?: JobTypeName) => {
  try {
    const params = omitBy({ job_type: jobType }, (value) => !value);
    return await apiService<NotesData>({ method: "get", url: "/jobs/additional-notes/", params });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const getConfig = async () => {
  try {
    return await apiService<CreateEditJobOptions>({ method: "get", url: "/jobs/create_edit_job_options/" });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const updateJobMarkers = async (id: number, data: JobMarkersDto) => {
  try {
    return await apiService({ method: "patch", url: `/jobs/${id}/marker/`, data });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const addJobDocuments = async (id: number, data: FormData) => {
  try {
    return await apiService({ method: "patch", url: `/jobs/update_job_document/${id}/`, data, isMultipart: true });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const deleteJobDocuments = async ({ docId, jobId }: { docId: number | string; jobId?: number }) => {
  try {
    return await apiService({
      method: "delete",
      url: `/jobs/delete_document/${docId}/?jobId=${jobId}`,
      isMultipart: true,
    });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export interface RepairReviewResponse {
  layer_count: number | null;
  wall_lose: number | null;
  error: string | null;
}

export const getRepairReview = async ({ data, isMultipart }: { data: unknown; isMultipart?: boolean }) => {
  try {
    return await apiService<RepairReviewResponse>({
      method: "post",
      url: "/jobs/job_calcs_preview_view/",
      data,
      isMultipart,
      timeout: 90000,
    });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const getJobCalcsPreviewView = async ({ data, isMultipart }: { data: unknown; isMultipart?: boolean }) => {
  try {
    return await apiService<JobCalcsPreviewResponse>({
      method: "post",
      url: "/jobs/job_calcs_preview_view/",
      data,
      isMultipart,
      timeout: 90000,
    });
  } catch (error) {
    throw getErrorMessage(error);
  }
};
